import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { fetchInventoryItemsByRestaurant } from './inventoryApi';

const AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+$/i;
const AXIOS_NETWORK_MESSAGE = /^network error$/i;

/** User-facing message from API error body (e.g. 409 conflict). */
export function getApiErrorMessage(error, fallback = 'An error occurred') {
  const data = error?.response?.data;
  if (data != null && typeof data === 'object') {
    const raw =
      data.errorMessage ??
      data.ErrorMessage ??
      data.message ??
      data.Message ??
      data.error ??
      data.Error ??
      data.details;
    if (Array.isArray(raw)) {
      const text = raw.filter(Boolean).join(', ');
      if (text) return text;
    }
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  if (!error?.response) {
    if (error?.code === 'ECONNABORTED') {
      return 'Request timed out. Check that the API server is running.';
    }
    return 'Could not reach the API server. Check REACT_APP_API_COMPANY_URL and that the backend is running.';
  }
  const axiosMsg = error?.message;
  if (axiosMsg && !AXIOS_STATUS_MESSAGE.test(axiosMsg) && !AXIOS_NETWORK_MESSAGE.test(axiosMsg)) {
    return axiosMsg;
  }
  return fallback;
}

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function resolveFoodOrCateringIds(foodItemId, cateringItemId) {
  const fid =
    foodItemId != null && foodItemId !== '' ? Number(foodItemId) : null;
  const cid =
    cateringItemId != null && cateringItemId !== ''
      ? Number(cateringItemId)
      : null;
  if ((!fid || fid <= 0) && (!cid || cid <= 0)) {
    throw new Error('Either foodItemId or cateringItemId is required.');
  }
  return { fid, cid };
}

export function normalizeRecipeItems(items = []) {
  return (items || [])
    .map((it) => {
      const inventoryItemId = Number(it.inventoryItemId ?? it.Inventory_Item_Id);
      const quantity = Number(it.quantity ?? it.Quantity);
      if (
        !inventoryItemId ||
        inventoryItemId <= 0 ||
        Number.isNaN(quantity) ||
        quantity < 0
      ) {
        return null;
      }
      const entry = { inventoryItemId, quantity };
      const recipeId = it.recipeId ?? it.Recipe_Id;
      if (recipeId != null && recipeId !== '') {
        entry.recipeId = Number(recipeId);
      }
      return entry;
    })
    .filter(Boolean);
}

export function normalizeRecipeSteps(steps = []) {
  return (steps || [])
    .map((st, idx) => {
      const instruction = String(st.instruction ?? st.Instruction ?? '').trim();
      if (!instruction) return null;
      const step = {
        stepNumber: st.stepNumber != null ? Number(st.stepNumber) : idx + 1,
        instruction,
      };
      const stepId = st.stepId ?? st.Step_Id;
      if (stepId != null && stepId !== '') {
        step.stepId = Number(stepId);
      }
      const notes = String(st.notes ?? st.Notes ?? '').trim();
      if (notes) step.notes = notes;
      return step;
    })
    .filter(Boolean);
}

/**
 * Build body for POST /restaurants/food-items/recipes.
 * Either foodItemId or cateringItemId is required (not both required, but at least one).
 */
export function buildCreateRecipePayload({
  foodItemId,
  cateringItemId,
  restaurantId,
  createdById,
  items = [],
  steps = [],
}) {
  const { fid, cid } = resolveFoodOrCateringIds(foodItemId, cateringItemId);
  if (!restaurantId || Number(restaurantId) <= 0) {
    throw new Error('restaurantId is required.');
  }
  if (!createdById || Number(createdById) <= 0) {
    throw new Error('createdById is required.');
  }

  const payload = {
    restaurantId: Number(restaurantId),
    createdById: Number(createdById),
    items: normalizeRecipeItems(items),
    steps: normalizeRecipeSteps(steps),
  };

  if (fid && fid > 0) payload.foodItemId = fid;
  if (cid && cid > 0) payload.cateringItemId = cid;

  return payload;
}

/**
 * Build body for PUT /restaurants/food-items/recipes (replace items + steps).
 */
export function buildUpdateRecipePayload({
  foodItemId,
  cateringItemId,
  items = [],
  steps = [],
}) {
  const { fid, cid } = resolveFoodOrCateringIds(foodItemId, cateringItemId);
  const payload = {
    items: normalizeRecipeItems(items),
    steps: normalizeRecipeSteps(steps),
  };
  if (fid && fid > 0) payload.foodItemId = fid;
  if (cid && cid > 0) payload.cateringItemId = cid;
  return payload;
}

/** Options for recipe ingredient dropdown from restaurant inventory. */
export function mapInventoryRowToRecipeIngredientOption(row) {
  const id = row?.inventoryItemId ?? row?.Inventory_Item_Id;
  if (id == null) return null;
  const itemName = row?.itemName ?? row?.Item_Name ?? '';
  const unitSymbol = row?.unitSymbol ?? row?.Unit_Symbol ?? '';
  const unitName = row?.unitName ?? row?.Unit_Name ?? '';
  return {
    inventoryItemId: id,
    Inventory_Item_Id: id,
    groceryProductId: row?.groceryId ?? row?.groceryProductId ?? null,
    itemName,
    Item_Name: itemName,
    productName: itemName,
    productCode: row?.productCode ?? '',
    groceryCategoryName: row?.groceryCategoryName ?? '',
    unitSymbol,
    unitName,
  };
}

/** Legacy: grocery catalog option (uses grocery product id). */
export function mapGroceryToRecipeIngredientOption(product) {
  const id = product?.id ?? product?.groceryProductId;
  if (id == null) return null;
  const productName = product.productName ?? product.Product_Name ?? '';
  const unitSymbol = product.unitSymbol ?? product.Unit_Symbol ?? '';
  const unitName = product.unitName ?? product.Unit_Name ?? '';
  return {
    inventoryItemId: id,
    Inventory_Item_Id: id,
    groceryProductId: id,
    itemName: productName,
    Item_Name: productName,
    productName,
    productCode: product.productCode ?? product.ProductCode ?? '',
    groceryCategoryName:
      product.groceryCategoryName ?? product.Grocery_Category_Name ?? '',
    unitSymbol,
    unitName,
  };
}

export function formatRecipeIngredientName(row) {
  if (!row) return '';
  const id = row.inventoryItemId ?? row.Inventory_Item_Id;
  return (
    row.itemName ??
    row.Item_Name ??
    row.productName ??
    (id != null ? `Item ${id}` : '')
  );
}

export function formatRecipeIngredientLabel(row) {
  if (!row) return '';
  const name = formatRecipeIngredientName(row);
  const unit = String(row.unitSymbol ?? row.unitName ?? '').trim();
  return unit ? `${name} (${unit})` : name;
}

/** Unit for a recipe line item (API field or grocery catalog lookup). */
export function getRecipeIngredientUnit(item, inventoryRows = []) {
  const fromItem =
    item?.unitSymbol ??
    item?.Unit_Symbol ??
    item?.unitName ??
    item?.Unit_Name ??
    item?.unit ??
    item?.Unit;
  if (fromItem != null && String(fromItem).trim()) return String(fromItem).trim();

  const id = item?.inventoryItemId ?? item?.Inventory_Item_Id;
  if (id == null) return '';
  const row = (inventoryRows || []).find(
    (inv) =>
      String(inv.inventoryItemId ?? inv.Inventory_Item_Id ?? inv.groceryProductId) ===
      String(id)
  );
  if (!row) return '';
  return String(row.unitSymbol ?? row.unitName ?? '').trim();
}

/** Display quantity with unit, e.g. "1.000 kg". */
export function formatRecipeIngredientQuantity(item, inventoryRows = []) {
  const raw = item?.quantity ?? item?.Quantity;
  if (raw == null || String(raw).trim() === '') {
    return { quantity: '—', unit: '' };
  }
  const unit = getRecipeIngredientUnit(item, inventoryRows);
  const quantity = String(raw).trim();
  return { quantity, unit };
}

/**
 * Recipe ingredient picker: GET /restaurants/:restaurantId/inventory-items.
 */
export async function fetchRecipeIngredientOptions(restaurantId) {
  const rid = Number(
    restaurantId ?? localStorage.getItem('restaurantId') ?? 0
  );
  if (!rid || Number.isNaN(rid)) return [];
  const rows = await fetchInventoryItemsByRestaurant(rid);
  return rows
    .map(mapInventoryRowToRecipeIngredientOption)
    .filter(Boolean)
    .sort((a, b) =>
      String(a.itemName || '').localeCompare(String(b.itemName || ''))
    );
}

/**
 * POST /restaurants/food-items/recipes
 * Request: { foodItemId? | cateringItemId?, restaurantId, createdById, items[], steps[] }
 */
export async function createFoodItemRecipe(payload) {
  const body = buildCreateRecipePayload(payload);
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantsFoodItemsRecipes,
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    }
  );
  return response.data ?? {};
}

/**
 * PUT /restaurants/food-items/recipes
 * Request: { foodItemId? | cateringItemId?, items[], steps[] }
 * Response: { items, steps, message }
 */
export async function updateFoodItemRecipe(payload) {
  const body = buildUpdateRecipePayload(payload);
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantsFoodItemsRecipes,
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    }
  );
  return response.data ?? {};
}

/**
 * GET /restaurants/food-items/recipes?foodItemId= | ?cateringItemId=
 * Pass a number (foodItemId) or { foodItemId, cateringItemId }.
 */
export async function fetchFoodItemRecipe(foodItemIdOrOptions, cateringItemId) {
  let foodItemId;
  let cateringId = cateringItemId;
  if (foodItemIdOrOptions != null && typeof foodItemIdOrOptions === 'object') {
    foodItemId = foodItemIdOrOptions.foodItemId;
    cateringId = foodItemIdOrOptions.cateringItemId ?? cateringId;
  } else {
    foodItemId = foodItemIdOrOptions;
  }

  const params = {};
  const fid = foodItemId != null && foodItemId !== '' ? Number(foodItemId) : null;
  const cid = cateringId != null && cateringId !== '' ? Number(cateringId) : null;
  if (fid && fid > 0) params.foodItemId = fid;
  if (cid && cid > 0) params.cateringItemId = cid;

  if (!params.foodItemId && !params.cateringItemId) {
    throw new Error('Either foodItemId or cateringItemId is required.');
  }

  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantsFoodItemsRecipes, {
    params,
    headers: authHeaders(),
  });
  return response.data ?? {};
}
